import { motion, AnimatePresence } from 'framer-motion';

interface FileReceivedModalProps {
  isOpen: boolean;
  fileName: string;
  fileSize: number;
  onDownload: () => void;
  onClose: () => void;
}

/**
 * Modal shown when a file is received from a peer
 */
export function FileReceivedModal({ 
  isOpen, 
  fileName, 
  fileSize, 
  onDownload, 
  onClose 
}: FileReceivedModalProps) {
  if (!isOpen) return null;

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-blue-500/20 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            {/* File Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg 
                  className="w-10 h-10 text-blue-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-3">
                File Received!
              </h2>
              <p className="text-gray-400 text-base leading-relaxed mb-4">
                You've received a file. Click the button below to download it to your device.
              </p>
              
              {/* File Details */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="text-left space-y-2">
                  <div className="flex items-start">
                    <span className="text-gray-500 text-sm mr-2 flex-shrink-0">File:</span>
                    <span className="text-white text-sm font-medium break-all">{fileName}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 text-sm mr-2">Size:</span>
                    <span className="text-white text-sm">{formatFileSize(fileSize)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-xl transition-all"
              >
                Decline
              </button>
              <button
                onClick={onDownload}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                Download
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
